/** @format */

import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import Card from '../../components/Card/Card';
import CardHeader from '../../components/Heading/CardHeaderExt';
import CardIcon from '../../components/Card/CardIcon';
import PermIdentity from '@material-ui/icons/PermIdentity';
import CardBody from '../../components/Card/CardBody';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import InputForm from '../../components/Form/InputForm';
import CardFooter from '../../components/Card/CardFooter';
import Button from '@material-ui/core/Button';
import I18n from '../../components/Translate/I18n';
import { useDispatch } from 'react-redux';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import GeoLocationStateSelect from '../../components/Adescom/GeoLocationStateSelect';
import TariffSelect from '../../components/Adescom/TariffSelect';
import { PaymentDeadlineCodeSelect } from '../../components/Adescom/PaymentDeadlineCodeSelect';
import { OperatorMvneTypeMap, useOperatorAccess } from '../../modules/lib/hooks/useOperatorType';
import { SimpleSelect } from '../../components/Adescom/SimpleSelect';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { GeoLocationStateLoader } from '../../modules/getlocationstate/GeoLocationLoader';
import { Checkbox, FormControlLabel, makeStyles } from '@material-ui/core';
import adescomStyle from '../../assets/jss/adescom/adescomStyle';
import CustomTextButtom from '../../components/Buttons/CustomTextButton';
import { Auth } from '../../modules/auth/Auth';
import { roles, UserRoleHelper } from '../../helpers/UserRoleHelper';
import PlatformResellerLanguage from './Components/PlatformResellerLanguage';
import PlatformResellerInvoicing from './Components/PlatformResellerInvoicing';
import { TARIFF_TYPE_HOSTING, TARIFF_TYPE_MTR } from '../../interfaces/billing/TariffInterface';
import PlatformResellerVat from './Components/PlatformResellerVat';
import PlatformResellerCountryCode from './Components/PlatformResellerCountryCode';
import PlatformResellerPayerCountryCode from './Components/PlatformResellerPayerCountryCode';

// const _ = require('underscore')

export type PlatformResellerSendType = {
    mainname: string;
    firstname?: string;
    mail?: string;
    nip: string;
    // externalidSecondary?: number;
    street: string;
    apartment?: string;
    city: string;
    state: string;
    country: string;
    house: string;
    zip: string;
    active: number;
    parentResellerId: number | null;
    syncDefaultPortinMsisdnIndicatedRangeId?: number;
    paymentDeadlineCodeId?: number | undefined;
    tariffId?: number;
    hostTariffId?: number;
    agreementNumber?: string;
    ecorpNumber?: string;
    ecorpCostNumber?: string;
    paymentDisposition?: number;
    bankaccount?: string;
    accountingNumber?: string;
    organizationUnit?: string;
    rpt: string | null;
    ftpUser: string | null;
    requireVcOnProductActivation: boolean;
    platformBillingCustomer: { level: number };
    fupBasePeriod: number;
    fupMonitorDays: number;
    fupMonitorDaysHitSendSms: boolean;
    fupMonitorDaysHitSmsMsg: string | null;
    fupMonitorCheckDays: number;
    fupMonitorCheckDaysSendSms: boolean;
    fupMonitorCheckDaysFailsSmsMsg: string | null;
    fupMonitorCheckDaysFailsViolation: number;
    fupMonitorCheckDaysFailsViolationSendSms: boolean;
    fupMonitorCheckDaysFailsViolationSmsMsg: string | null;
    useType: number;
    allowConfirmationCode: boolean;
    eSimPreActivateAllowed: boolean;
    defaultSystemLanguageId: number;
    invoiceLanguageId: number;
    resellerAvailableLanguages: string;
    currencyId: number;

    payerMainname: string;
    payerFirstname?: string;
    payerMail?: string;
    payerNip: string;
    payerStreet: string;
    payerApartment?: string;
    payerCity: string;
    payerState: string;
    payerCountry: string;
    payerHouse: string;
    payerZip: string;
    vat: string;
    countryCode: string;
    payerCountryCode: string;
    invoicingEmail: string;
};

export type PlatformResellerModifyType = {
    editId?: string | null;
    parentId?: string | null;
    outputObject?: PlatformResellerSendType;
    savePlatformReseller: (outputObject: PlatformResellerSendType) => void;
};

enum CustomItemType {
    HostTariff,
    Tariff,
    State,
    IndicatedRange,
    PaymentDeadlineCode,
    Vat,
    PaymentDisposition,
    PayerState,
    PayerVat,
}

export const PlatformResellerModify = (props: PlatformResellerModifyType): React.ReactElement => {
    const savePlatformReseller = props.savePlatformReseller;
    const dispatch = useDispatch();
    const [statesLoaded, setStatesLoaded] = useState<boolean>(false);
    const itemLoaders = useMemo(() => {
        return new ItemLoaders(dispatch);
    }, [dispatch]);
    const geoLocationStateLoader = useMemo(() => {
        return new GeoLocationStateLoader(itemLoaders);
    }, [itemLoaders]);

    useEffect((): void => {
        geoLocationStateLoader.loadGeoLocationStates({
            callback: (): void => {
                setStatesLoaded(true);
            },
            filters: [],
            orders: [],
        });
    }, [geoLocationStateLoader]);

    const [outputObject, setOutputObject] = useState<PlatformResellerSendType>(
        props.outputObject ??
            ({
                mainname: '',
                nip: '',
                street: '',
                city: '',
                state: '',
                country: '',
                house: '',
                zip: '',
                payerMainname: '',
                payerNip: '',
                payerStreet: '',
                payerCity: '',
                payerState: '',
                payerCountry: '',
                payerHouse: '',
                payerZip: '',
                vat: '',
                countryCode: '',
                payerCountryCode: ''
            } as PlatformResellerSendType)
    );
    const [platformResellerStatus, setPlatformResellerStatus] = useState<any>({});
    const id = useMemo((): string | null => {
        if (props.editId) {
            return props.editId;
        }

        if (props.parentId) {
            return props.parentId;
        }

        return null;
    }, [props.editId, props.parentId]);
    const isOPL = useOperatorAccess(OperatorMvneTypeMap.OPL);
    const isMainReseller = !props.outputObject?.parentResellerId;
    const saveReseller = (): void => {
        props.savePlatformReseller({ ...outputObject });
    };

    const updateCallback = (
        event: React.FormEvent<HTMLInputElement>,
        stateName: string,
        typeField: string,
        typeCallback: string,
        required: boolean,
        vMax: number,
        vMin?: number
    ): void => {
        if (!id) {
            return;
        }
        const tmp = { ...outputObject };
        FormGeneralHelper.validateFormsHook(
            tmp,
            setOutputObject,
            platformResellerStatus,
            setPlatformResellerStatus,
            event,
            stateName,
            typeField,
            require,
            vMax,
            vMin
        );
    };

    const setStateForOutput = useCallback(
        (tmpSelectedState: string | number, type: CustomItemType) => {
            const tmpOutputObject = outputObject;

            switch (type) {
                case CustomItemType.State:
                    tmpOutputObject.state = tmpSelectedState as string;
                    break;
                case CustomItemType.PayerState:
                    tmpOutputObject.payerState = tmpSelectedState as string;
                    break;
                case CustomItemType.Tariff:
                    tmpOutputObject.tariffId = tmpSelectedState as number;
                    break;
                case CustomItemType.HostTariff:
                    tmpOutputObject.hostTariffId = tmpSelectedState as number;
                    break;
                case CustomItemType.IndicatedRange:
                    tmpOutputObject.syncDefaultPortinMsisdnIndicatedRangeId =
                        typeof tmpSelectedState === 'string' ? parseInt(tmpSelectedState) : tmpSelectedState;
                    break;
                case CustomItemType.PaymentDeadlineCode:
                    tmpOutputObject.paymentDeadlineCodeId = typeof tmpSelectedState === 'string' ? parseInt(tmpSelectedState) : tmpSelectedState;
                    break;
                // case CustomItemType.Vat:
                //     tmpOutputObject.vat = typeof tmpSelectedState === 'string' ? parseInt(tmpSelectedState) : tmpSelectedState;
                //     break;
                case CustomItemType.PaymentDisposition:
                    tmpOutputObject.paymentDisposition = typeof tmpSelectedState === 'string' ? parseInt(tmpSelectedState) : tmpSelectedState;
                    break;
                default:
                    break;
            }

            setOutputObject({ ...tmpOutputObject });
        },
        [outputObject]
    );

    const onCheckBoxEvent = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const name = event.target.name;
        const value = event.target.value;
        const checked = event.target.checked;
        switch (name) {
            case 'requireVcOnProductActivation':
                setOutputObject({ ...outputObject, requireVcOnProductActivation: checked });
                break;
            case 'fupMonitorCheckMonthFailsSendSms':
                setOutputObject({ ...outputObject, fupMonitorCheckDaysFailsViolationSendSms: checked });
                break;
            case 'fupMonitorCheckDaysSendSms':
                setOutputObject({ ...outputObject, fupMonitorCheckDaysSendSms: checked });
                break;
            case 'fupMonitorDaysHitSendSms':
                setOutputObject({ ...outputObject, fupMonitorDaysHitSendSms: checked });
                break;
            case 'allowConfirmationCode':
                setOutputObject({ ...outputObject, allowConfirmationCode: checked });
                break;
            case 'eSimPreActivateAllowed':
                setOutputObject({ ...outputObject, eSimPreActivateAllowed: checked });
                break;
            default:
                break;
        }
    };

    const changeDefaultLanguage = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const value = event.target.value;
        if (value) {
            const parsed = parseInt(value);

            if (parsed) {
                setOutputObject({ ...outputObject, defaultSystemLanguageId: parsed });
            }
        }
    };

    const changeCurrency = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const value = event.target.value;
        if (value) {
            const parsed = parseInt(value);

            if (parsed) {
                setOutputObject({ ...outputObject, currencyId: parsed });
            }
        }
    };

    const changeAvailableLanguagesCallback = (langs: Array<number>): void => {
        setOutputObject({ ...outputObject, resellerAvailableLanguages: JSON.stringify(langs) });
    };

    const changeInvoiceLanguageCallback = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const value = event.target.value;
        if (value) {
            const parsed = parseInt(value);

            if (parsed) {
                setOutputObject({ ...outputObject, invoiceLanguageId: parsed });
            }
        }
    };

    const changeVatCallback = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const value = event.target.value;
        if (value) {
            const parsed = value;

            if (parsed) {
                setOutputObject({ ...outputObject, vat: parsed });
            }
        }
    };

    const changeCountryCodeCallback = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const value = event.target.value;
        if (value) {
            const parsed = value;

            if (parsed) {
                setOutputObject({ ...outputObject, countryCode: parsed });
            }
        }
    };

    const changePayerCountryCodeCallback = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const value = event.target.value;
        if (value) {
            const parsed = value;

            if (parsed) {
                setOutputObject({ ...outputObject, payerCountryCode: parsed });
            }
        }
    };

    return (
        <React.Fragment>
            <Card>
                <CardBody>
                    {outputObject && (
                        <GridContainer>
                            <GridItem xs={12}>
                                <Card>
                                    <CardHeader trans="platform_reseller:h.basic_info" />
                                    <CardBody>
                                        <GridContainer spacing={1}>
                                            <GridItem xs={12} md={6}>
                                                <InputForm
                                                    stateName={'mainname'}
                                                    callback={updateCallback}
                                                    status={platformResellerStatus['mainname']}
                                                    typeField={'text'}
                                                    displayFieldName={'platform_reseller:pr.Name'}
                                                    required={true}
                                                    value={outputObject?.mainname}
                                                    vMin={-1}
                                                    vMax={256}
                                                    typeCallback={'string'}
                                                    autoComplete="new-mainname"
                                                />
                                            </GridItem>
                                            <GridItem xs={12} md={6}>
                                                <InputForm
                                                    stateName={'mail'}
                                                    callback={updateCallback}
                                                    status={platformResellerStatus['mail']}
                                                    typeField={'email'}
                                                    displayFieldName={'platform_reseller:pr.Email'}
                                                    required={false}
                                                    value={outputObject?.mail}
                                                    vMin={-1}
                                                    vMax={256}
                                                    typeCallback={'string'}
                                                    autoComplete="new-mail"
                                                />
                                            </GridItem>
                                            <GridItem xs={12} md={6}>
                                                <InputForm
                                                    stateName={'country'}
                                                    callback={updateCallback}
                                                    status={platformResellerStatus['country']}
                                                    typeField={'text'}
                                                    displayFieldName={'platform_reseller:pr.Country'}
                                                    required={true}
                                                    value={outputObject?.country}
                                                    vMin={-1}
                                                    vMax={256}
                                                    typeCallback={'string'}
                                                    autoComplete="new-country"
                                                />
                                            </GridItem>
                                            <GridItem xs={12} md={6}>
                                                {statesLoaded && (
                                                    <GeoLocationStateSelect
                                                        nullFlag={true}
                                                        label={'platform_reseller:pr.State'}
                                                        required={false}
                                                        callback={(e): void => {
                                                            const item = geoLocationStateLoader.getItemById(e?.target.value);
                                                            setStateForOutput(item?.stateName || '', CustomItemType.State);
                                                        }}
                                                        selectedValue={geoLocationStateLoader.getIdByName(outputObject?.state) || undefined}
                                                    />
                                                )}
                                            </GridItem>
                                            <GridItem xs={12} md={6}>
                                                <InputForm
                                                    stateName={'city'}
                                                    callback={updateCallback}
                                                    status={platformResellerStatus['city']}
                                                    typeField={'text'}
                                                    displayFieldName={'platform_reseller:pr.City'}
                                                    required={true}
                                                    value={outputObject?.city}
                                                    vMin={-1}
                                                    vMax={256}
                                                    typeCallback={'string'}
                                                    autoComplete="new-city"
                                                />
                                            </GridItem>
                                            <GridItem xs={12} md={6}>
                                                <InputForm
                                                    stateName={'street'}
                                                    callback={updateCallback}
                                                    status={platformResellerStatus['street']}
                                                    typeField={'text'}
                                                    displayFieldName={'platform_reseller:pr.Street'}
                                                    required={true}
                                                    value={outputObject?.street}
                                                    vMin={-1}
                                                    vMax={256}
                                                    typeCallback={'string'}
                                                    autoComplete="new-street"
                                                />
                                            </GridItem>
                                            <GridItem xs={12} md={4}>
                                                <InputForm
                                                    stateName={'house'}
                                                    callback={updateCallback}
                                                    status={platformResellerStatus['house']}
                                                    typeField={'text'}
                                                    displayFieldName={'platform_reseller:pr.House'}
                                                    required={true}
                                                    value={outputObject?.house}
                                                    vMin={-1}
                                                    vMax={256}
                                                    typeCallback={'string'}
                                                    autoComplete="new-house"
                                                />
                                            </GridItem>
                                            <GridItem xs={12} md={4}>
                                                <InputForm
                                                    stateName={'apartment'}
                                                    callback={updateCallback}
                                                    status={platformResellerStatus['apartment']}
                                                    typeField={'text'}
                                                    displayFieldName={'platform_reseller:pr.Apartment'}
                                                    required={false}
                                                    value={outputObject?.apartment}
                                                    vMin={-1}
                                                    vMax={256}
                                                    typeCallback={'string'}
                                                    autoComplete="new-apartment"
                                                />
                                            </GridItem>
                                            <GridItem xs={12} md={4}>
                                                <InputForm
                                                    stateName={'zip'}
                                                    callback={updateCallback}
                                                    status={platformResellerStatus['zip']}
                                                    typeField={'text'}
                                                    displayFieldName={'platform_reseller:pr.Zip'}
                                                    required={true}
                                                    value={outputObject?.zip}
                                                    vMin={-1}
                                                    vMax={256}
                                                    typeCallback={'string'}
                                                    autoComplete="new-zip"
                                                />
                                            </GridItem>
                                            <GridItem xs={12} md={4}>
                                                <InputForm
                                                    stateName={'invoicingEmail'}
                                                    callback={updateCallback}
                                                    status={platformResellerStatus['invoicingEmail']}
                                                    typeField={'text'}
                                                    displayFieldName={'platform_reseller:pr.invoicingEmail'}
                                                    required={true}
                                                    value={outputObject?.invoicingEmail}
                                                    vMin={-1}
                                                    vMax={256}
                                                    typeCallback={'string'}
                                                    autoComplete="new-invoicingEmail"
                                                />
                                            </GridItem>
                                            <GridItem xs={8}>
                                                <PlatformResellerLanguage
                                                    platformResellerId={props.editId}
                                                    platformReseller={outputObject}
                                                    changeDefaultLanguageCallback={changeDefaultLanguage}
                                                    changeAvailableLanguagesCallback={changeAvailableLanguagesCallback}
                                                />
                                            </GridItem>
                                            <GridItem xs={12}>
                                                <PlatformResellerInvoicing
                                                    isOPL={isOPL}
                                                    platformResellerId={props.editId}
                                                    platformReseller={outputObject}
                                                    changeCurrencyCallback={changeCurrency}
                                                    changeInvoiceLanguageCallback={changeInvoiceLanguageCallback}
                                                    changeVatCallback={changeVatCallback}
                                                    changeCountryCodeCallback={changeCountryCodeCallback}
                                                />
                                            </GridItem>
                                        </GridContainer>
                                    </CardBody>
                                </Card>
                            </GridItem>
                            <GridItem xs={12}>
                                <Card>
                                    <CardHeader trans="platform_reseller:h.detail_info" />
                                    <CardBody>
                                        <GridContainer spacing={1}>
                                            <GridItem xs={12} md={4}>
                                                <InputForm
                                                    stateName={'nip'}
                                                    callback={updateCallback}
                                                    status={platformResellerStatus['nip']}
                                                    typeField={'text'}
                                                    displayFieldName={'platform_reseller:pr.Nip'}
                                                    required={true}
                                                    value={outputObject?.nip}
                                                    vMin={-1}
                                                    vMax={256}
                                                    typeCallback={'string'}
                                                    autoComplete="new-nip"
                                                />
                                            </GridItem>
                                            {isOPL && Auth.isRootReseller() && (
                                                <GridItem xs={12} md={4}>
                                                    <InputForm
                                                        stateName={'agreementNumber'}
                                                        callback={updateCallback}
                                                        status={platformResellerStatus['agreementNumber']}
                                                        typeField={'text'}
                                                        displayFieldName={'platform_reseller:pr.agreement_number'}
                                                        required={true}
                                                        value={outputObject?.agreementNumber}
                                                        vMin={-1}
                                                        vMax={256}
                                                        typeCallback={'string'}
                                                        autoComplete="new-agreement_number"
                                                    />
                                                </GridItem>
                                            )}
                                            {isOPL && (
                                                <GridItem xs={12} md={4}>
                                                    <InputForm
                                                        stateName={'rpt'}
                                                        callback={updateCallback}
                                                        status={platformResellerStatus['rpt']}
                                                        typeField={'rpt'}
                                                        displayFieldName={'platform_reseller:pr.rpt'}
                                                        required={true}
                                                        value={outputObject.rpt ? outputObject.rpt.trim() : ''}
                                                        vMin={5}
                                                        vMax={5}
                                                        typeCallback={'string'}
                                                        autoComplete="new-rpt"
                                                    />
                                                </GridItem>
                                            )}
                                            {isOPL && Auth.isRootReseller() && (
                                                <GridItem xs={12} md={6}>
                                                    <InputForm
                                                        stateName={'ecorpNumber'}
                                                        callback={updateCallback}
                                                        status={platformResellerStatus['ecorpNumber']}
                                                        typeField={'text'}
                                                        displayFieldName={'platform_reseller:pr.ecorp_number'}
                                                        required={true}
                                                        value={outputObject?.ecorpNumber}
                                                        vMin={-1}
                                                        vMax={256}
                                                        typeCallback={'string'}
                                                        autoComplete="new-ecorpNumber"
                                                    />
                                                </GridItem>
                                            )}
                                            {isOPL && Auth.isRootReseller() && (
                                                <GridItem xs={12} md={6}>
                                                    <InputForm
                                                        stateName={'ecorpCostNumber'}
                                                        callback={updateCallback}
                                                        status={platformResellerStatus['ecorpCostNumber']}
                                                        typeField={'text'}
                                                        displayFieldName={'platform_reseller:pr.ecorp_cost_number'}
                                                        required={true}
                                                        value={outputObject?.ecorpCostNumber}
                                                        vMin={-1}
                                                        vMax={256}
                                                        typeCallback={'string'}
                                                        autoComplete="new-ecorpCostNumber"
                                                    />
                                                </GridItem>
                                            )}
                                            {Auth.isRootReseller() && (
                                                <>
                                                    <GridItem xs={12} md={6}>
                                                        <InputForm
                                                            stateName={'bankaccount'}
                                                            callback={updateCallback}
                                                            status={platformResellerStatus['bankaccount']}
                                                            typeField={'text'}
                                                            displayFieldName={'platform_reseller:pr.bankaccount'}
                                                            required={false}
                                                            value={outputObject?.bankaccount}
                                                            vMin={-1}
                                                            vMax={256}
                                                            typeCallback={'string'}
                                                            autoComplete="new-bankaccount"
                                                        />
                                                    </GridItem>
                                                    <GridItem xs={12} md={6}>
                                                        <InputForm
                                                            stateName={'organizationUnit'}
                                                            callback={updateCallback}
                                                            status={platformResellerStatus['organizationUnit']}
                                                            typeField={'text'}
                                                            displayFieldName={'platform_reseller:pr.organizational_unit'}
                                                            required={false}
                                                            value={outputObject?.organizationUnit}
                                                            vMin={-1}
                                                            vMax={256}
                                                            typeCallback={'string'}
                                                            autoComplete="new-organization-unit"
                                                        />
                                                    </GridItem>
                                                </>
                                            )}
                                            {Auth.isRootReseller() && (
                                                <GridItem xs={12} md={6}>
                                                    <TariffSelect
                                                        label={'platform_reseller:pr.tariff'}
                                                        required={true}
                                                        selectedValue={outputObject?.tariffId}
                                                        type={TARIFF_TYPE_MTR}
                                                        callback={(e): void => {
                                                            setStateForOutput(e?.target.value, CustomItemType.Tariff);
                                                        }}
                                                        dispatchName={'test'}
                                                    />
                                                </GridItem>
                                            )}
                                            {!isMainReseller && Auth.isRootReseller() && (
                                                <GridItem xs={12} md={6}>
                                                    <TariffSelect
                                                        label={'platform_reseller:pr.host_tariff'}
                                                        required={true}
                                                        selectedValue={outputObject?.hostTariffId}
                                                        type={TARIFF_TYPE_HOSTING}
                                                        callback={(e): void => {
                                                            setStateForOutput(e?.target.value, CustomItemType.HostTariff);
                                                        }}
                                                        dispatchName={'test'}
                                                    />
                                                </GridItem>
                                            )}
                                            {!isMainReseller && Auth.isRootReseller() && (
                                                <React.Fragment>
                                                    <GridItem xs={12} md={6}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={outputObject.requireVcOnProductActivation}
                                                                    name="requireVcOnProductActivation"
                                                                    onChange={onCheckBoxEvent}
                                                                />
                                                            }
                                                            label={<I18n trans="platform_reseller:pr.require_vc_on_product_activation" />}
                                                        />
                                                    </GridItem>
                                                    <GridItem xs={12} md={6} />
                                                    <GridItem xs={12} md={6}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={outputObject.allowConfirmationCode}
                                                                    name="allowConfirmationCode"
                                                                    onChange={onCheckBoxEvent}
                                                                />
                                                            }
                                                            label={<I18n trans="platform_reseller:pr.esim_allow_confirmation_code" />}
                                                        />
                                                    </GridItem>
                                                    <GridItem xs={12} md={6} />
                                                    <GridItem xs={12} md={6}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={outputObject.eSimPreActivateAllowed}
                                                                    name="eSimPreActivateAllowed"
                                                                    onChange={onCheckBoxEvent}
                                                                />
                                                            }
                                                            label={<I18n trans="platform_reseller:pr.e_eim_pre_activate_allowed" />}
                                                        />
                                                    </GridItem>
                                                </React.Fragment>
                                            )}
                                            {isOPL && Auth.isRootReseller() && (
                                                <GridItem xs={12} md={6}>
                                                    <PaymentDeadlineCodeSelect
                                                        label={'platform_reseller:pr.payment_deadline_code'}
                                                        required={true}
                                                        callback={(e): void => {
                                                            setStateForOutput(e?.target.value, CustomItemType.PaymentDeadlineCode);
                                                        }}
                                                        selectedValue={outputObject?.paymentDeadlineCodeId}
                                                    />
                                                </GridItem>
                                            )}
                                            {Auth.isRootReseller() && (
                                                <GridItem xs={12} md={6}>
                                                    <div>
                                                        <SimpleSelect
                                                            label={'platform_reseller:pr.payment_disposition'}
                                                            required={true}
                                                            elements={[
                                                                { name: 'platform_reseller:pr.compensation', value: 1 },
                                                                { name: 'platform_reseller:pr.transfer', value: 2 },
                                                            ]}
                                                            callback={(e): void => {
                                                                setStateForOutput(e?.target.value, CustomItemType.PaymentDisposition);
                                                            }}
                                                            dispatchName={'test'}
                                                            selectedValue={outputObject?.paymentDisposition}
                                                        />
                                                    </div>
                                                </GridItem>
                                            )}
                                            {Auth.isRootReseller() && (
                                                <GridItem xs={12} md={6}>
                                                    <InputForm
                                                        stateName={'accountingNumber'}
                                                        callback={updateCallback}
                                                        status={platformResellerStatus['accountingNumber']}
                                                        typeField={'text'}
                                                        displayFieldName={'platform_reseller:pr.accounting_number'}
                                                        required={false}
                                                        value={outputObject?.accountingNumber}
                                                        vMin={-1}
                                                        vMax={256}
                                                        typeCallback={'string'}
                                                        autoComplete="new-accounting-number"
                                                    />
                                                </GridItem>
                                            )}
                                            {Auth.isRootReseller() && (
                                                <GridItem xs={12} md={6}>
                                                    <InputForm
                                                        stateName={'ftpUser'}
                                                        callback={updateCallback}
                                                        status={platformResellerStatus['ftpUser']}
                                                        typeField={'text'}
                                                        displayFieldName={'platform_reseller:pr.ftp_user'}
                                                        required={false}
                                                        value={outputObject?.ftpUser}
                                                        vMin={-1}
                                                        vMax={256}
                                                        typeCallback={'string'}
                                                        autoComplete="new-ftp=user"
                                                    />
                                                </GridItem>
                                            )}
                                        </GridContainer>
                                    </CardBody>
                                </Card>
                            </GridItem>
                            {Auth.isRootReseller() && (
                                <GridItem xs={12}>
                                    <Card>
                                        <CardHeader trans="platform_reseller:h.fup" />
                                        <CardBody>
                                            <GridContainer>
                                                <GridItem xs={6} md={4} lg={3}>
                                                    <InputForm
                                                        stateName={'fupBasePeriod'}
                                                        callback={updateCallback}
                                                        status={platformResellerStatus['fupBasePeriod']}
                                                        typeField={'number_ext'}
                                                        displayFieldName={'platform_reseller:pr.fup_base_period'}
                                                        required={false}
                                                        value={outputObject?.fupBasePeriod}
                                                        vMin={1}
                                                        vMax={-1}
                                                        typeCallback={'number'}
                                                        autoComplete="fupBasePeriod=user"
                                                    />
                                                </GridItem>
                                            </GridContainer>
                                            <GridContainer>
                                                <GridItem xs={6} md={4} lg={3}>
                                                    <InputForm
                                                        stateName={'fupMonitorDays'}
                                                        callback={updateCallback}
                                                        status={platformResellerStatus['fupMonitorDays']}
                                                        typeField={'number_ext'}
                                                        displayFieldName={'platform_reseller:pr.fup_monitor_days'}
                                                        required={false}
                                                        value={outputObject?.fupMonitorDays}
                                                        vMin={1}
                                                        vMax={-1}
                                                        typeCallback={'number'}
                                                        autoComplete="fupMonitorDays=user"
                                                    />
                                                </GridItem>
                                                <GridItem xs={12}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={outputObject.fupMonitorDaysHitSendSms}
                                                                name="fupMonitorDaysHitSendSms"
                                                                onChange={onCheckBoxEvent}
                                                            />
                                                        }
                                                        label={<I18n trans="platform_reseller:pr.fup_monitor_days_hit_send_sms" />}
                                                    />
                                                </GridItem>
                                                <GridItem xs={12}>
                                                    <InputForm
                                                        stateName={'fupMonitorDaysHitSmsMsg'}
                                                        callback={updateCallback}
                                                        status={platformResellerStatus['fupMonitorDaysHitSmsMsg']}
                                                        typeField={'text'}
                                                        displayFieldName={'platform_reseller:pr.fup_monitor_days_hit_send_msg'}
                                                        required={false}
                                                        value={outputObject?.fupMonitorDaysHitSmsMsg}
                                                        vMin={1}
                                                        vMax={-1}
                                                        typeCallback={'string'}
                                                        autoComplete="fupMonitorDaysHitSmsMsg=user"
                                                    />
                                                </GridItem>
                                            </GridContainer>
                                            <GridContainer>
                                                <GridItem xs={6} md={4} lg={3}>
                                                    <InputForm
                                                        stateName={'fupMonitorCheckDays'}
                                                        callback={updateCallback}
                                                        status={platformResellerStatus['fupMonitorCheckDays']}
                                                        typeField={'number_ext'}
                                                        displayFieldName={'platform_reseller:pr.fup_monitor_check_days'}
                                                        required={false}
                                                        value={outputObject?.fupMonitorCheckDays}
                                                        vMin={1}
                                                        vMax={-1}
                                                        typeCallback={'number'}
                                                        autoComplete="fupMonitorCheckDays=user"
                                                    />
                                                </GridItem>
                                                <GridItem xs={12}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={outputObject.fupMonitorCheckDaysSendSms}
                                                                name="fupMonitorCheckDaysSendSms"
                                                                onChange={onCheckBoxEvent}
                                                            />
                                                        }
                                                        label={<I18n trans="platform_reseller:pr.fup_monitor_days_send_sms" />}
                                                    />
                                                </GridItem>
                                                <GridItem xs={12}>
                                                    <InputForm
                                                        stateName={'fupMonitorCheckDaysFailsSmsMsg'}
                                                        callback={updateCallback}
                                                        status={platformResellerStatus['fupMonitorCheckDaysFailsSmsMsg']}
                                                        typeField={'text'}
                                                        displayFieldName={'platform_reseller:pr.fup_monitor_days_fails_send_sms'}
                                                        required={false}
                                                        value={outputObject?.fupMonitorCheckDaysFailsSmsMsg}
                                                        vMin={1}
                                                        vMax={-1}
                                                        typeCallback={'string'}
                                                        autoComplete="fupMonitorCheckDaysFailsSmsMsg=user"
                                                    />
                                                </GridItem>
                                            </GridContainer>
                                            <GridContainer>
                                                <GridItem xs={6} md={4} lg={3}>
                                                    <InputForm
                                                        stateName={'fupMonitorCheckDaysFailsViolation'}
                                                        callback={updateCallback}
                                                        status={platformResellerStatus['fupMonitorCheckDaysFailsViolation']}
                                                        typeField={'number_ext'}
                                                        displayFieldName={'platform_reseller:pr.fup_monitor_check_days_fails_violation'}
                                                        required={false}
                                                        value={outputObject?.fupMonitorCheckDaysFailsViolation}
                                                        vMin={1}
                                                        vMax={-1}
                                                        typeCallback={'number'}
                                                        autoComplete="fupMonitorCheckDaysFailsViolation=user"
                                                    />
                                                </GridItem>
                                                <GridItem xs={12}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={outputObject.fupMonitorCheckDaysFailsViolationSendSms}
                                                                name="fupMonitorCheckDaysFailsViolationSendSms"
                                                                onChange={onCheckBoxEvent}
                                                            />
                                                        }
                                                        label={<I18n trans="platform_reseller:pr.fup_monitor_check_days_fails_violation_send_sms" />}
                                                    />
                                                </GridItem>
                                                <GridItem xs={12}>
                                                    <InputForm
                                                        stateName={'fupMonitorCheckDaysFailsViolationSmsMsg'}
                                                        callback={updateCallback}
                                                        status={platformResellerStatus['fupMonitorCheckDaysFailsViolationSmsMsg']}
                                                        typeField={'text'}
                                                        displayFieldName={'platform_reseller:pr.fup_monitor_check_days_fails_violation_sms_msg'}
                                                        required={false}
                                                        value={outputObject?.fupMonitorCheckDaysFailsViolationSmsMsg}
                                                        vMin={1}
                                                        vMax={-1}
                                                        typeCallback={'string'}
                                                        autoComplete="fupMonitorCheckDaysFailsViolationSmsMsg=user"
                                                    />
                                                </GridItem>
                                            </GridContainer>
                                        </CardBody>
                                    </Card>
                                </GridItem>
                            )}
                            <GridItem xs={12}>
                                <Card>
                                    <CardHeader trans="platform_reseller:h.payer_info" />
                                    <CardBody>
                                        <GridContainer spacing={1}>
                                            <GridItem xs={12} md={6}>
                                                <InputForm
                                                    stateName={'payerMainname'}
                                                    callback={updateCallback}
                                                    status={platformResellerStatus['payerMainname']}
                                                    typeField={'text'}
                                                    displayFieldName={'platform_reseller:pr.Name'}
                                                    required={true}
                                                    value={outputObject?.payerMainname}
                                                    vMin={-1}
                                                    vMax={256}
                                                    typeCallback={'string'}
                                                    autoComplete="new-payer-mainname"
                                                />
                                            </GridItem>
                                            <GridItem xs={12} md={6}>
                                                <InputForm
                                                    stateName={'payerMail'}
                                                    callback={updateCallback}
                                                    status={platformResellerStatus['payerMail']}
                                                    typeField={'email'}
                                                    displayFieldName={'platform_reseller:pr.Email'}
                                                    required={false}
                                                    value={outputObject?.payerMail}
                                                    vMin={-1}
                                                    vMax={256}
                                                    typeCallback={'string'}
                                                    autoComplete="new-payer-mail"
                                                />
                                            </GridItem>
                                            <GridItem xs={12} md={6}>
                                                <InputForm
                                                    stateName={'payerCountry'}
                                                    callback={updateCallback}
                                                    status={platformResellerStatus['payerCountry']}
                                                    typeField={'text'}
                                                    displayFieldName={'platform_reseller:pr.Country'}
                                                    required={true}
                                                    value={outputObject?.payerCountry}
                                                    vMin={-1}
                                                    vMax={256}
                                                    typeCallback={'string'}
                                                    autoComplete="new-payer-country"
                                                />
                                            </GridItem>
                                            <GridItem xs={12} md={6}>
                                                {statesLoaded && (
                                                    <GeoLocationStateSelect
                                                        nullFlag={true}
                                                        label={'platform_reseller:pr.State'}
                                                        required={false}
                                                        callback={(e): void => {
                                                            const item = geoLocationStateLoader.getItemById(e?.target.value);
                                                            setStateForOutput(item?.stateName || '', CustomItemType.PayerState);
                                                        }}
                                                        selectedValue={geoLocationStateLoader.getIdByName(outputObject?.state) || undefined}
                                                    />
                                                )}
                                            </GridItem>
                                            <GridItem xs={12} md={6}>
                                                <InputForm
                                                    stateName={'payerCity'}
                                                    callback={updateCallback}
                                                    status={platformResellerStatus['payerCity']}
                                                    typeField={'text'}
                                                    displayFieldName={'platform_reseller:pr.City'}
                                                    required={true}
                                                    value={outputObject?.payerCity}
                                                    vMin={-1}
                                                    vMax={256}
                                                    typeCallback={'string'}
                                                    autoComplete="new-payer-city"
                                                />
                                            </GridItem>
                                            <GridItem xs={12} md={6}>
                                                <InputForm
                                                    stateName={'payerStreet'}
                                                    callback={updateCallback}
                                                    status={platformResellerStatus['payerStreet']}
                                                    typeField={'text'}
                                                    displayFieldName={'platform_reseller:pr.Street'}
                                                    required={true}
                                                    value={outputObject?.payerStreet}
                                                    vMin={-1}
                                                    vMax={256}
                                                    typeCallback={'string'}
                                                    autoComplete="new-payer-street"
                                                />
                                            </GridItem>
                                            <GridItem xs={12} md={4}>
                                                <InputForm
                                                    stateName={'payerHouse'}
                                                    callback={updateCallback}
                                                    status={platformResellerStatus['payerHouse']}
                                                    typeField={'text'}
                                                    displayFieldName={'platform_reseller:pr.House'}
                                                    required={true}
                                                    value={outputObject?.payerHouse}
                                                    vMin={-1}
                                                    vMax={256}
                                                    typeCallback={'string'}
                                                    autoComplete="new-payer-house"
                                                />
                                            </GridItem>
                                            <GridItem xs={12} md={4}>
                                                <InputForm
                                                    stateName={'payerApartment'}
                                                    callback={updateCallback}
                                                    status={platformResellerStatus['payerApartment']}
                                                    typeField={'text'}
                                                    displayFieldName={'platform_reseller:pr.Apartment'}
                                                    required={false}
                                                    value={outputObject?.payerApartment}
                                                    vMin={-1}
                                                    vMax={256}
                                                    typeCallback={'string'}
                                                    autoComplete="new-payer-apartment"
                                                />
                                            </GridItem>
                                            <GridItem xs={12} md={4}>
                                                <InputForm
                                                    stateName={'payerZip'}
                                                    callback={updateCallback}
                                                    status={platformResellerStatus['payerZip']}
                                                    typeField={'text'}
                                                    displayFieldName={'platform_reseller:pr.Zip'}
                                                    required={true}
                                                    value={outputObject?.payerZip}
                                                    vMin={-1}
                                                    vMax={256}
                                                    typeCallback={'string'}
                                                    autoComplete="new-payer-zip"
                                                />
                                            </GridItem>
                                            <GridItem xs={12} md={4}>
                                                <InputForm
                                                    stateName={'payerNip'}
                                                    callback={updateCallback}
                                                    status={platformResellerStatus['payerNip']}
                                                    typeField={'text'}
                                                    displayFieldName={'platform_reseller:pr.Nip'}
                                                    required={true}
                                                    value={outputObject?.payerNip}
                                                    vMin={-1}
                                                    vMax={256}
                                                    typeCallback={'string'}
                                                    autoComplete="new-payer-nip"
                                                />
                                            </GridItem>
                                            {isOPL && Auth.isRootReseller() && (
                                                <GridItem xs={12} md={4}>
                                                    <PlatformResellerPayerCountryCode
                                                        platformResellerId={props.editId}
                                                        platformReseller={outputObject}
                                                        changeCountryCodeCallback={changePayerCountryCodeCallback}
                                                    />
                                                </GridItem>
                                            )}
                                        </GridContainer>
                                    </CardBody>
                                </Card>
                            </GridItem>
                        </GridContainer>
                    )}

                    {/*{!Auth.isRootReseller() && (*/}
                    {/*    <InputForm*/}
                    {/*        stateName={'firstname'}*/}
                    {/*        callback={updateCallback}*/}
                    {/*        status={platformResellerStatus['firstname']}*/}
                    {/*        typeField={'text'}*/}
                    {/*        displayFieldName={'platform_reseller:pr.FirstName'}*/}
                    {/*        required={false}*/}
                    {/*        value={outputObject?.firstname}*/}
                    {/*        vMin={-1}*/}
                    {/*        vMax={256}*/}
                    {/*        typeCallback={'string'}*/}
                    {/*        autoComplete="new-firstname"*/}
                    {/*    />*/}
                    {/*)}*/}

                    {/*<InputForm*/}
                    {/*    stateName={'externalidSecondary'}*/}
                    {/*    callback={updateCallback}*/}
                    {/*    status={platformResellerStatus['externalidSecondary']}*/}
                    {/*    typeField={'text'}*/}
                    {/*    displayFieldName={'platform_reseller:pr.externalidSecondary'}*/}
                    {/*    required={false}*/}
                    {/*    value={outputObject?.externalidSecondary}*/}
                    {/*    vMin={-1}*/}
                    {/*    vMax={256}*/}
                    {/*    typeCallback={'string'}*/}
                    {/*    autoComplete="new-externalidSecondary"*/}
                    {/*/>*/}

                    {/*{UserRoleHelper.checkRole(roles.ROLE_SUPERUSER) && (*/}
                    {/*    <>*/}
                    {/*        <GridItem ms={12} xs={12} md={6}>*/}
                    {/*            <IndicatedRangeSelect*/}
                    {/*                label={'platform_reseller:pr.indicated_range'}*/}
                    {/*                required={true}*/}
                    {/*                callback={(e): void => {*/}
                    {/*                    setStateForOutput(e?.target.value, CustomItemType.IndicatedRange);*/}
                    {/*                }}*/}
                    {/*                selectedValue={outputObject?.syncDefaultPortinMsisdnIndicatedRangeId}*/}
                    {/*            />*/}
                    {/*        </GridItem>*/}
                    {/*    </>*/}
                    {/*)}*/}
                </CardBody>
                <CardFooter>
                    <GridContainer>
                        <GridItem xs={10} />
                        <GridItem xs={2}>
                            {!Auth.isRootReseller() &&
                                (!props.editId ||
                                    (props.editId &&
                                        parseInt(props.editId.split('/')[props.editId.split('/').length - 1]) !==
                                            Auth.getUserPlatformResellerId())) &&
                                (!outputObject.platformBillingCustomer ||
                                    (outputObject.platformBillingCustomer &&
                                        outputObject.platformBillingCustomer.level &&
                                        Auth.getCustomerLevel() !== outputObject.platformBillingCustomer.level)) &&
                                UserRoleHelper.checkRole(roles.ROLE_PLATFORM_RESELLER_PUT) && (
                                    <CustomTextButtom
                                        callback={(): void => {
                                            saveReseller();
                                        }}
                                        color={'primary'}
                                        size={'lg'}
                                        label={'general:common.save'}
                                    />
                                )}

                            {Auth.isRootReseller() && (
                                <CustomTextButtom
                                    callback={(): void => {
                                        saveReseller();
                                    }}
                                    color={'primary'}
                                    size={'lg'}
                                    label={'general:common.save'}
                                />
                            )}
                        </GridItem>
                    </GridContainer>
                </CardFooter>
            </Card>
        </React.Fragment>
    );
};

export default PlatformResellerModify;
